import { useState } from "react";

import { AdminInventoryCodesEndpoints, fetcher } from "@olagg/api-hooks";
import {
  InventoryCode,
  InventoryCodesFilter,
  InventoryCodesUploadResponse,
} from "@olagg/db-types";
import { useToast } from "@chakra-ui/react";

const useInventoryCodes = (productId: string) => {
  const toast = useToast();
  const [codes, setCodes] = useState<InventoryCode[]>([]);
  const [count, setCount] = useState<number>(0);
  const [availableCount, setAvailableCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const all = async (filter: InventoryCodesFilter) => {
    setIsLoading(true);
    try {
      const response = await fetcher(AdminInventoryCodesEndpoints.all(productId, filter));
      setCodes(response.inventoryCodes || response.inventory_codes || []);
      setCount(response.meta?.count || 0);
      setAvailableCount(response.meta?.availableCount ?? response.meta?.available_count ?? 0);
      setTotalCount(response.meta?.totalCount ?? response.meta?.total_count ?? 0);
    } catch (error: any) {
      toast({
        title: 'Error',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error?.message || 'No se pudieron cargar los códigos',
        position: 'bottom-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadCodes = async (file: File): Promise<InventoryCodesUploadResponse | null> => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response: InventoryCodesUploadResponse = await fetcher(
        AdminInventoryCodesEndpoints.upload(productId, formData)
      );
      toast({
        title: 'Códigos importados',
        description: `${response.imported} códigos importados. ${response.skipped?.length || 0} saltados.`,
        status: 'success',
        duration: 4000,
        position: 'top-right',
      });
      return response;
    } catch (error: any) {
      toast({
        title: 'Error al subir el archivo',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error?.error || error?.errors || error?.message || 'No se pudo procesar el archivo',
        position: 'bottom-right',
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const deleteCode = async (codeId: number): Promise<boolean> => {
    try {
      await fetcher(AdminInventoryCodesEndpoints.delete(productId, codeId));
      toast({
        title: 'Código eliminado',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      return true;
    } catch (error: any) {
      toast({
        title: 'No se pudo eliminar el código',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error?.errors || error?.message || 'Error',
        position: 'bottom-right',
      });
      return false;
    }
  };

  return {
    codes,
    count,
    availableCount,
    totalCount,
    isLoading,
    isUploading,
    all,
    uploadCodes,
    deleteCode,
  };
};

export default useInventoryCodes;
